import { Interpolation, Theme } from '@emotion/react';
import { HydratedCallRecord } from '@weave/schema-gen-ts/dist/schemas/phone-exp/phone-records/v1/call_record.pb';
import { Blip } from '@weave/schema-gen-ts/dist/schemas/phone-exp/phone-records-ext/call-quality/v1/api.pb';
import { Task } from '@weave/schema-gen-ts/dist/schemas/task-center/shared/v1/task.pb';
import { Call } from '@weave/schema-gen-ts/dist/shared/call-intelligence/v2/call_intelligence.pb';

export type CallInsightPanelSource =
  | 'callIntel'
  | 'callHistoryContactPanel'
  | 'patientInsightsContactPanel'
  | 'dashboardUnhappyWidget'
  | 'callHistory'
  | 'taskCenter'
  | 'unhappyCallNotification'
  | 'callRecordTagNotification'
  | 'staffPerformance'
  | 'communicationAnalytics'
  | 'callInsightTrialTeaser'
  | 'airVoiceAnalytics';

export type CallInsightAccordionType =
  | 'callIntel'
  | 'tasks'
  | 'aboutThisCall'
  | 'callQuality'
  | 'conversation'
  | 'callBreakdown';

export type CallBreakdownTabType = 'conversationAnalysis' | 'callDetails' | 'callActivity';

export type CallBreakdownTabsVisibility = {
  showConversationAnalysis?: boolean;
  showCallDetails?: boolean;
  showCallActivity?: boolean;
};

export type PanelOutputEvents = {
  callAnalysisUpdated: { call: Call };
  taskDeleted: { call: Call; taskId: string };
  tasksUpdated: { call: Call; tasks: Task[] };
  callTagAdded: { callRecord: HydratedCallRecord; tagId: string };
  callTagRemoved: { callRecord: HydratedCallRecord; tagId: string };
  callTagsUpdated: { callRecord: HydratedCallRecord; tagIds: string[] };
  callRecordingDeleted: { channelId: string; locationId?: string };
};
export type PanelInputEvents = {
  callTagsExternalSync: { channelId: string; tagIds: string[] };
};

export type PanelEvents = PanelOutputEvents & PanelInputEvents;

export enum ScreenType {
  CALL_INSIGHTS = 'CALL_INSIGHTS',
  CALL_QUALITY = 'CALL_QUALITY',
  CALL_QUALITY_DRILLDOWN = 'CALL_QUALITY_DRILLDOWN',
  CALL_PATH_MAPPING = 'CALL_PATH_MAPPING',
}

export interface ScreenDataMap {
  [ScreenType.CALL_INSIGHTS]: undefined; // no payload
  [ScreenType.CALL_QUALITY]: undefined; // no payload
  [ScreenType.CALL_QUALITY_DRILLDOWN]: {
    label: string;
    blip: Blip;
    startTime: string;
    channelId: string;
    locationId: string;
  };
  [ScreenType.CALL_PATH_MAPPING]: {
    call: HydratedCallRecord;
  };
}

export type ScreenState<T extends ScreenType> = {
  screenType: T;
  data?: ScreenDataMap[T];
};

export interface ScreenBaseProps<T extends ScreenType = ScreenType> {
  source: CallInsightPanelSource;
  panelStyles?: Interpolation<Theme>;
  data?: ScreenDataMap[T];
  showBackButton?: boolean;
  showCloseButton?: boolean;
  setScreen: (params: { screenType: ScreenType; data?: ScreenDataMap[ScreenType] }) => void;
  onClose?: () => void;
}

export type ScreenComponentMap = {
  [K in ScreenType]: React.ComponentType<ScreenBaseProps<K>>;
};
